import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, User, Phone, MessageCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface SignupFormProps {
  onSwitchToLogin: () => void;
  onClose: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSwitchToLogin, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    realName: '',      // 실명 (필수)
    churchName: '',    // 교회명 (필수)
    phoneNumber: '',   // 휴대폰 번호 (필수)
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { setAuth, setLoading, isLoading } = useAuthStore();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }
    
    if (!formData.realName.trim()) {
      newErrors.realName = '실명을 입력해주세요.';
    } else if (formData.realName.trim().length < 2) {
      newErrors.realName = '실명은 2자 이상이어야 합니다.';
    }
    
    if (!formData.churchName.trim()) {
      newErrors.churchName = '교회명을 입력해주세요.';
    } else if (formData.churchName.trim().length < 2) {
      newErrors.churchName = '교회명은 2자 이상이어야 합니다.';
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = '휴대폰 번호를 입력해주세요.';
    } else if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = '올바른 휴대폰 번호를 입력해주세요. (예: 010-1234-5678)';
    }

    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식을 입력해주세요.';
    }

    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      newErrors.password = '비밀번호는 6자 이상이어야 합니다.';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // 유효성 검사 실패 시 첫 번째 에러를 토스트로 표시
      const firstError = Object.values(errors)[0];
      if (firstError) {
        toast.error(firstError, {
          duration: 3000,
          icon: <AlertCircle className="w-5 h-5 text-red-500" />,
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
          },
        });
      }
      return;
    }

    setLoading(true);

    try {
      console.log('📝 회원가입 시작:', formData.email);
      
      const response = await authAPI.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        realName: formData.realName,
        churchName: formData.churchName,
        phoneNumber: formData.phoneNumber,
      });
      
      console.log('✅ 회원가입 성공, 자동 로그인 처리 중...');
      const { user, accessToken, refreshToken } = response.data;
      
      // 자동 로그인 처리
      setAuth(user, accessToken, refreshToken, true);
      console.log('🔐 자동 로그인 완료');
      
      // 성공 메시지 표시
      toast.success('회원가입이 완료되었습니다! 환영합니다! 🎉', {
        duration: 3000,
        style: {
          background: '#f0fdf4',
          color: '#166534',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
      
      onClose();
    } catch (error: any) {
      console.error('❌ 회원가입 실패:', error);
      
      const errorData = error.response?.data;
      const statusCode = error.response?.status;
      
      // 사용자 친화적 에러 메시지 생성
      let errorMessage = '회원가입에 실패했습니다.';
      let errorField = null;
      
      if (statusCode === 409) {
        errorMessage = '이미 가입된 이메일입니다. 로그인을 시도해주세요.';
        errorField = errorData?.field || 'email';
      } else if (statusCode === 400) {
        // 백엔드에서 제공하는 구체적인 메시지 사용
        errorMessage = errorData?.message || '입력 정보를 확인해주세요.';
        errorField = errorData?.field;
        
        // 필드별 구체적인 안내
        if (errorField === 'email') {
          errorMessage = '올바른 이메일 형식을 입력해주세요.';
        } else if (errorField === 'password') {
          errorMessage = '비밀번호는 6자 이상이어야 합니다.';
        } else if (errorField === 'phoneNumber') {
          errorMessage = '올바른 휴대폰 번호를 입력해주세요. (예: 010-1234-5678)';
        } else if (errorField === 'realName') {
          errorMessage = '실명을 입력해주세요.';
        } else if (errorField === 'churchName') {
          errorMessage = '교회명을 입력해주세요.';
        }
      } else if (statusCode >= 500) {
        errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      // 개발자/관리자 소통을 위한 상세 에러 정보
      console.group('🚨 회원가입 에러 상세 정보');
      console.log('📧 사용자 입력 이메일:', formData.email);
      console.log('👤 사용자 입력 이름:', formData.name);
      console.log('📱 사용자 입력 휴대폰:', formData.phoneNumber);
      console.log('⛪ 사용자 입력 교회명:', formData.churchName);
      console.log('🔢 HTTP 상태 코드:', statusCode);
      console.log('📝 에러 메시지:', errorMessage);
      console.log('🎯 에러 필드:', errorField);
      console.log('🔍 에러 코드:', errorData?.code);
      console.log('📋 상세 에러 목록:', errorData?.details);
      console.log('🌐 요청 URL:', error.config?.url);
      console.log('⏰ 발생 시간:', new Date().toISOString());
      console.groupEnd();
      
      // 사용자에게는 간단한 메시지만 표시
      console.log('❌ 회원가입 에러 (사용자용):', errorMessage);
      
      // 토스트 알림으로 에러 메시지 표시
      toast.error(errorMessage, {
        duration: 4000,
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
      
      // 특정 필드에 에러 표시
      if (errorField) {
        setErrors({
          [errorField]: errorMessage,
        });
      } else {
        setErrors({
          general: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKakaoLogin = () => {
    // 카카오 클라이언트 ID 확인
    const kakaoClientId = import.meta.env.VITE_KAKAO_CLIENT_ID;
    
    if (!kakaoClientId) {
      console.error('❌ VITE_KAKAO_CLIENT_ID가 설정되지 않았습니다.');
      alert('카카오 로그인 설정 오류가 발생했습니다. 관리자에게 문의해주세요.');
      return;
    }
    
    // 카카오 로그인 URL로 리다이렉트
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${kakaoClientId}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/kakao/callback')}&response_type=code`;
    console.log('🔑 카카오 로그인 URL 생성:', { clientId: kakaoClientId?.substring(0, 10) + '...', redirectUri: window.location.origin + '/auth/kakao/callback' });
    window.location.href = kakaoAuthUrl;
  };

  return (
    <div className="w-full max-w-md mx-auto p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg p-2">
          <img 
            src="/images/logo.png" 
            alt="e2b 로고" 
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">회원가입</h2>
        <p className="text-sm sm:text-base text-slate-600">e2b 커뮤니티에 참여하세요</p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            이름 *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.name ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="이름을 입력하세요"
              required
            />
          </div>
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            실명 *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              value={formData.realName}
              onChange={(e) => setFormData({ ...formData, realName: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.realName ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="본명을 입력하세요"
              required
            />
          </div>
          {errors.realName && <p className="mt-1 text-sm text-red-600">{errors.realName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            교회명 *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              value={formData.churchName}
              onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.churchName ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="소속 교회를 입력하세요"
              required
            />
          </div>
          {errors.churchName && <p className="mt-1 text-sm text-red-600">{errors.churchName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            휴대폰 번호 *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.phoneNumber ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="010-1234-5678"
              required
            />
          </div>
          {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            이메일 *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.email ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="이메일을 입력하세요"
              required
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            전화번호 (선택)
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
              placeholder="010-1234-5678"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            비밀번호 *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.password ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="비밀번호를 입력하세요"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            비밀번호 확인 *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all ${
                errors.confirmPassword ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="비밀번호를 다시 입력하세요"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white py-3 rounded-xl font-semibold transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {isLoading ? '회원가입 중...' : '회원가입'}
        </button>
      </form>

      <div className="my-6 flex items-center">
        <div className="flex-1 border-t border-slate-300"></div>
        <span className="px-4 text-sm text-slate-500">또는</span>
        <div className="flex-1 border-t border-slate-300"></div>
      </div>

      <button
        onClick={handleKakaoLogin}
        className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-800 py-3 rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg"
      >
        <MessageCircle size={20} />
        카카오로 간편 가입
      </button>

      <div className="mt-6 text-center">
        <p className="text-slate-600">
          이미 계정이 있으신가요?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-rose-500 hover:text-rose-600 font-semibold"
          >
            로그인
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
